import React, { useMemo } from 'react'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { InfoText } from '@e3dc-react/shell/Elements/InfoText'
import { Button, CircularProgress, PaletteColor, Stack, useTheme } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { J2Caution } from '@e3dc-react/icons'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import useModals from 'src/Hooks/useModals'
import { useEm360PairingFormInitializer } from 'src/Libs/installations/hooks'
import useActiveInstallationContext from 'src/Libs/installations/hooks/use-active-installation-context'
import { DeviceLinkModel, ProductSerialnumber } from 'src/Libs/installations'
import { useDeviceLinkActions } from 'src/Libs/installations/hooks/installation-configuration/em360/use-device-link-actions'
import Typography from '@e3dc-react/shell/Elements/Typography/Typography'
import { Chip } from 'src/Components'
import { isUndefined } from 'lodash'

interface Props {
  installationId?: number
  emcDeviceLink?: DeviceLinkModel
  productSerialnumbers: ProductSerialnumber[]
  isProductSerialnumbersLoading: boolean
  isProductSerialnumbersError: boolean
  isEmcOnline: boolean
  isEmcConnectionLoading: boolean
  isEmcConnectionError: boolean
}

const SerialnumberPanel: React.FC<Props> = ({
  installationId,
  emcDeviceLink,
  productSerialnumbers,
  isProductSerialnumbersLoading,
  isProductSerialnumbersError,
  isEmcOnline,
  isEmcConnectionLoading,
  isEmcConnectionError,
}) => {
  const { t } = useCustomTranslation()
  const { openEmcPairingDialog, openConfirmationModal } = useModals()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { validationSchema } = useEm360PairingFormInitializer()
  const { refreshDeviceLinks } = useActiveInstallationContext()
  const { updateDeviceLinkMutation, updateEmcMiddlewareMutation } = useDeviceLinkActions()

  const handleDeleteConfirmation = (): void => {
    openConfirmationModal({
      onConfirm: handleDelete,
      buttonText: [t('common.cancel'), t('common.confirm')],
      text: t('installation-configuration.general-settings.handle-delete-confirmation-dialog.text'),
      title: t('installation-configuration.general-settings.handle-delete-confirmation-dialog.title'),
    })
  }

  const em360Serialnumbers = useMemo(() => {
    if (!productSerialnumbers.length) {
      return undefined
    }

    return productSerialnumbers[0]
  }, [productSerialnumbers])

  const onScanQrCode = (
    data: string | null,
    setPairingId: (value: string) => void,
    setStartQrScanner: (value: boolean) => void,
    setScannerError: (value: string) => void
  ): void => {
    setPairingId('')
    setScannerError('')
    if (data) {
      const validated = validationSchema.safeParse({ pairingId: data })

      if (!validated.success) {
        const message = validated.error.issues[0]?.message ?? t('installation-configuration.general-settings.save.error')
        setScannerError(message)
        setStartQrScanner(false)
        return
      }

      setPairingId(validated.data.pairingId)
      setStartQrScanner(false)
    }
  }

  const handlePairingClick = (): void => {
    if (!installationId) return

    openEmcPairingDialog({
      pairingId: emcDeviceLink?.deviceId ?? '',
      isNewEmc: !emcDeviceLink,
      useDefaultValidation: false,
      customTitle: t('installation-configuration.em360.pairing-dialog.title'),
      customInputLabel: t('installation-configuration.em360.pairing-dialog.input-label'),
      submitPairingId: async (value, setError) => {
        const parsed = validationSchema.safeParse({ pairingId: value })

        if (!parsed.success) {
          const message = parsed.error.issues[0]?.message ?? t('installation-configuration.general-settings.save.error')
          setError?.(message)
          return false
        }

        const pairingId = parsed.data.pairingId
        const isSerialNumber = /^\d{12}$/.test(pairingId)
        if (!emcDeviceLink?.id) {
          setError?.(t('installation-configuration.general-settings.save.error'))
          return false
        }

        updateEmcMiddlewareMutation
          .mutateAsync({
            installationId,
            deviceLinkId: emcDeviceLink?.id.toString(),
            payload: isSerialNumber ? { serialno: pairingId } : { productSerialno: pairingId },
          })
          .then(async () => {
            await refreshDeviceLinks()
            enqueueSuccessStack(t('installation-configuration.general-settings.save.success'))
          })
          .catch(error => {
            let message = t('installation-configuration.general-settings.save.error')
            if (error?.response?.data?.errno === 21027 || error?.response?.data?.errno === 405) {
              message = t('installation-configuration.em360.error.serialnumber-already-linked')
            }

            if (error?.response?.data?.errno === 404) {
              message = t('installation-configuration.em360.error.serialnumber-not-found')
            }

            enqueueErrorStack(message)
            console.error('could not save emc pairing', error)
          })

        return true
      },
      onScanQrCode,
    })
  }

  const handleDelete = (): void => {
    if (!installationId || !emcDeviceLink) return

    updateDeviceLinkMutation
      .mutateAsync({ installationId, deviceLinkId: emcDeviceLink.id.toString(), payload: { deviceId: null } })
      .then(async () => {
        await refreshDeviceLinks()
        enqueueSuccessStack(t('installation-configuration.general-settings.delete.success'))
      })
      .catch(error => {
        enqueueErrorStack(t('installation-configuration.general-settings.delete.error'))
        console.error('could not delete em360', error)
      })
  }

  return (
    <AccordionItem title={'common.serialnumber'} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={2}
        justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        {isProductSerialnumbersLoading || isProductSerialnumbersError ? (
          <ErrorLoadingDisplay isLoading={isProductSerialnumbersLoading} isError={isProductSerialnumbersError} />
        ) : (
          <Stack direction={'row'} gap={2} justifyContent={{ xs: 'space-between', sm: 'flex-start' }} sx={{ width: '100%' }}>
            <InfoText
              title={'installation-configuration.em360.product-serialnumber'}
              value={em360Serialnumbers?.productSerialno ?? '-'}
              titleTop
              titleVariant={'subtitle2'}
              valueVariant={'body2'}
            />
            <InfoText
              title={'installation-configuration.em360.e3dc-serialnumber'}
              value={em360Serialnumbers?.serialno ?? '-'}
              titleTop
              titleVariant={'subtitle2'}
              valueVariant={'body2'}
            />
          </Stack>
        )}
        {emcDeviceLink?.deviceId ? (
          <Button variant={'contained'} sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }} onClick={handleDeleteConfirmation}>
            {t('installation-configuration.em360.remove-em360')}
          </Button>
        ) : (
          <Button variant={'contained'} sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }} onClick={handlePairingClick}>
            {t('installation-configuration.em360.pair-em360')}
          </Button>
        )}
      </Stack>
      {!!emcDeviceLink?.deviceId && (
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ mt: 5 }}>
          <Typography>{'installation-configuration.em360.connection-state'}</Typography>
          <ConnectionStatusChip isOnline={isEmcOnline} isLoading={isEmcConnectionLoading} isError={isEmcConnectionError} />
        </Stack>
      )}
    </AccordionItem>
  )
}

const ErrorLoadingDisplay: React.FC<{ isLoading: boolean; isError: boolean }> = ({ isLoading, isError }) => {
  return (
    <Stack alignItems={'center'} justifyContent={'center'} sx={{ width: '100%' }}>
      {isLoading && <CircularProgress />}
      {isError && (
        <InfoText value={'installation-configuration.em360.product-serialnumber-error'} icon={<J2Caution />} sx={{ color: 'error.main' }} />
      )}
    </Stack>
  )
}

const ConnectionStatusChip: React.FC<{ isOnline: boolean; isLoading: boolean; isError: boolean }> = ({ isOnline, isLoading, isError }) => {
  const theme = useTheme()

  const getPaletteColor = (): PaletteColor | undefined => {
    if (isLoading || isUndefined(isOnline)) {
      return undefined
    } else if (isOnline) {
      return theme.palette.primary
    } else {
      return theme.palette.error
    }
  }

  const getTextKey = (): string => {
    if (isError) {
      return 'common.unknown'
    } else if (isOnline) {
      return 'common.online'
    } else {
      return 'common.offline'
    }
  }

  return <Chip paletteColor={getPaletteColor()} textKey={getTextKey()} isLoading={isLoading || isUndefined(isOnline)} />
}

export default SerialnumberPanel
